import { useCallback } from 'react'
import { useReactiveVar } from '@apollo/client'
import { analyticsClient } from 'shared/analytics'
import { CONSENT_POLICY_VERSION } from '../constants'
import { consentVar, isConsentDialogOpenVar } from '../store'
import type { ConsentCategories, ConsentChoice } from '../types'
import { writeConsentCookie } from '../utils/consent-cookie'
import { reportConsent } from '../utils/report-consent'

/**
 * Read and change the cookie consent (FE-330).
 *
 * `grant` writes the first-party cookie, updates the reactive var, closes the
 * dialog and hands the choice to the analytics client: the queued events may
 * go out now, and `updateAnalyticsConsent` goes first so the backend knows
 * whether it may deliver them to GA4 and Meta.
 */
export const useConsent = () => {
  const consent = useReactiveVar(consentVar)

  const grant = useCallback((categories: ConsentCategories) => {
    const choice: ConsentChoice = {
      ...categories,
      version: CONSENT_POLICY_VERSION,
      grantedAt: new Date().toISOString()
    }

    writeConsentCookie(choice)
    consentVar(choice)
    isConsentDialogOpenVar(false)
    analyticsClient.setConsentDecided(true)
    reportConsent(choice)
  }, [])

  const acceptAll = useCallback(
    () => grant({ analytics: true, marketing: true }),
    [grant]
  )
  const rejectOptional = useCallback(
    () => grant({ analytics: false, marketing: false }),
    [grant]
  )
  const openDialog = useCallback(() => isConsentDialogOpenVar(true), [])

  return {
    consent,
    isDecided: consent !== null,
    grant,
    acceptAll,
    rejectOptional,
    openDialog
  }
}
